/**
 * Поле ввода: Enter — отправить на десктопе, перенос строки на тач-устройствах.
 * Shift+Enter — перенос строки везде. Отправка кнопкой-стрелкой — везде.
 *
 * УПОМИНАНИЯ (@): символ `@` открывает выпадающий список участников с
 * фильтрацией по мере набора. Выбор вставляет читаемое имя `@Имя` в текст И
 * записывает упоминание в отдельный список `mentions` — отправка идёт уже
 * как `RichSegment[]`, чтобы `body`/`formatted_body`/`m.mentions` совпали.
 * Имя, набранное вручную без выбора, упоминанием НЕ считается (как в Element):
 * без выбора из списка нет ни push, ни подсветки — это осознанно.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { tf, useT, useTF } from "../i18n";
import { isShellFilePickerAvailable, pickFilesViaShell } from "../shell/desktopFilePicker";
import { shellPickErrorText } from "./shellPickerError";

import type { MemberInfo, RichSegment, TextMark } from "../matrix/roomActions";
import { palette, s } from "./styles";
import { EmojiPicker } from "./EmojiPicker";
import { insertMention, useCaretAfterCommit } from "./mentionInsert";
import {
  IconCheck,
  IconClose,
  IconFile,
  IconImage,
  IconMic,
  IconPlus,
  IconSend,
  IconSmile,
  IconStop,
  IconTrash,
} from "./icons";
import { color, font, radius, space } from "./theme";
import { composerCss } from "./composerCss";
import { useAuthedMedia } from "../matrix/media";
import { useMatrixClient } from "../matrix/clientContext";
import { draftKey, loadDraft, saveDraft, clearDraft } from "../matrix/drafts";
import { useTypingSender } from "../matrix/typing";

/**
 * Миниатюра в баннере ответа: 32px, object-fit: cover.
 * Грузим через useAuthedMedia — Bearer-заголовок обязателен.
 */
function ComposerThumb({ mxc }: { mxc: string }) {
  const client = useMatrixClient();
  const { url } = useAuthedMedia(client, mxc);
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      style={{
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        objectFit: "cover",
        flex: "0 0 auto",
      }}
      data-testid="composer-thumb"
    />
  );
}

/** Одно подтверждённое упоминание в черновике (id null = @room). */
interface DraftMention {
  userId: string | null;
  /** Читаемое имя в тексте, включая `@`, напр. `@Антон`. */
  displayName: string;
  /** Сколько символов это имя занимает в `text` (для сборки сегментов). */
  length: number;
}

/**
 * Разбить текст черновика на сегменты, подставив подтверждённые упоминания.
 *
 * Сканируем `mentions` в порядке вставки: для каждого ищем его `displayName`
 * в необработанной части текста и вырезаем текст до него. Совпадение, которое
 * пользователь стёр или переписал, просто не находится — и такое упоминание
 * тихо выпадает (push не отправляется), не ломая остальное.
 */
interface DraftFormat {
  start: number;
  end: number;
  mark: TextMark;
}

function sameMark(a: TextMark, b: TextMark): boolean {
  return a === b || (typeof a === "object" && typeof b === "object" && a.color === b.color);
}

/* ============ ПРОДОЛЖЕНИЕ СПИСКА ПО Shift+Enter ============
 *
 * Постановка Антона 2026-09-18, п.5: «Enter отправляет, поэтому
 * продолжение списка возможно только через Shift+Enter — сейчас он
 * обрывает список».
 *
 * ДОПОЛНЕНИЕ 2026-10-05: Enter отправляет ТОЛЬКО на десктопе (без тач-ввода).
 * На тач-устройствах (телефон/планшет) Enter — перенос строки, отправка
 * только кнопкой-стрелкой (как в iOS). Продолжение списка оставлено на
 * Shift+Enter: это уже привычное сочетание.
 *
 * До правки Shift+Enter вставлял голый \n, и человек
 * каждый раз дописывал «2.» руками.
 *
 * Функция ЧИСТАЯ и экспортируется специально: правило «что считать
 * пунктом списка» проверяется юнит-тестом без DOM и без клавиатуры.
 *
 * Маркеры РОВНО ТЕ ЖЕ, что понимает рендер простого текста в ленте
 * (renderPlain в Timeline.tsx): `1.` / `1)` и `-` / `*`. Расширять набор здесь
 * нельзя в одиночку: композер продолжал бы список, который лента потом
 * нарисует простым текстом.
 */
const ORDERED_ITEM_RE = /^([ \t]*)(\d{1,9})([.)])([ \t]+)(.*)$/;
const BULLET_ITEM_RE = /^([ \t]*)([-*])([ \t]+)(.*)$/;

export type ListContinuation =
  /** Вставить этот текст (перевод строки + следующий маркер). */
  | { type: "continue"; insert: string }
  /** Пункт пуст — стереть маркер и выйти из списка. */
  | { type: "exit"; removeChars: number };

/**
 * Что сделать по Shift+Enter в конце строки `line`.
 *
 * @param line текст текущей строки ОТ НАЧАЛА ДО КУРСОРА.
 * @returns null — это не пункт списка, пусть браузер вставит обычный \n.
 */
export function listContinuationFor(line: string): ListContinuation | null {
  const ordered = ORDERED_ITEM_RE.exec(line);
  if (ordered) {
    const [, indent, num, delim, gap, content] = ordered;
    // Пустой пункт + Shift+Enter = выход из списка (постановка).
    if (content.trim() === "") return { type: "exit", removeChars: line.length };
    // Номер следующего пункта — текущий + 1. Отступ, разделитель
    // (точка или скобка) и ширина пробела сохраняются: список остаётся
    // выровненным и в том же стиле, как его начал человек.
    return { type: "continue", insert: `\n${indent}${Number(num) + 1}${delim}${gap}` };
  }
  const bullet = BULLET_ITEM_RE.exec(line);
  if (bullet) {
    const [, indent, marker, gap, content] = bullet;
    if (content.trim() === "") return { type: "exit", removeChars: line.length };
    return { type: "continue", insert: `\n${indent}${marker}${gap}` };
  }
  return null;
}

/** Build mention and formatting segments without ever accepting HTML input. */
function buildSegments(text: string, mentions: DraftMention[], formats: DraftFormat[]): RichSegment[] {
  const mentionAt = new Map<number, DraftMention>();
  let searchFrom = 0;
  for (const m of mentions) {
    const at = text.indexOf(m.displayName, searchFrom);
    if (at < 0) continue;
    mentionAt.set(at, m);
    searchFrom = at + m.length;
  }

  const boundaries = new Set<number>([0, text.length]);
  const insideMention = (p: number) => [...mentionAt].some(([at, m]) => p > at && p < at + m.length);
  for (const f of formats) {
    if (!insideMention(f.start)) boundaries.add(f.start);
    if (!insideMention(f.end)) boundaries.add(f.end);
  }
  for (const [at, m] of mentionAt) { boundaries.add(at); boundaries.add(at + m.length); }
  const points = [...boundaries].filter((p) => p >= 0 && p <= text.length).sort((a, b) => a - b);
  const segments: RichSegment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    if (end <= start) continue;
    const mention = mentionAt.get(start);
    if (mention && end === start + mention.length) {
      segments.push({ type: "mention", userId: mention.userId, displayName: mention.displayName });
      continue;
    }
    const marks = formats.filter((f) => f.start <= start && f.end >= end).map((f) => f.mark);
    segments.push({ type: "text", text: text.slice(start, end), ...(marks.length ? { marks } : {}) });
  }
  return segments.length ? segments : [{ type: "text", text }];
}

/** CSS classes for the safe, text-only live formatting preview. */
function previewMarkClasses(marks: TextMark[] | undefined): string | undefined {
  if (!marks?.length) return undefined;
  const classes = marks
    .filter((mark) => typeof mark === "string")
    .map((mark) => `alg-preview-${mark}`);
  // buildRichParts nests marks in array order, so the first color is the
  // innermost one and wins. Match that result if ranges contain two colors.
  const firstColor = marks.find((mark) => typeof mark === "object");
  if (firstColor && typeof firstColor === "object") classes.push(`alg-preview-color-${firstColor.color}`);
  return classes.join(" ") || undefined;
}

/** Есть ли непустой текст (упоминание без текста — валидное сообщение). */
function hasContent(text: string, mentions: DraftMention[]): boolean {
  return text.trim().length > 0 || mentions.length > 0;
}

/** Человекочитаемый размер файла. */
function formatBytes(n: number): string {
  if (n < 1024) return tf("common.sizeBytes", { size: n });
  if (n < 1024 * 1024) return tf("common.sizeKb", { size: (n / 1024).toFixed(1) });
  return tf("common.sizeMb", { size: (n / (1024 * 1024)).toFixed(1) });
}

/** Local preview for a pending image. Object URLs are always revoked. */
function PendingImagePreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  if (!url) return null;
  return <img className="alg-pending-attachment-image" src={url} alt={file.name} />;
}

export function Composer({
  disabled,
  roomId,
  members,
  canMentionRoom,
  onSend,
  onSendFile,
  onSendVoice,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
}: {
  disabled: boolean;
  /** Меняется при переключении комнаты — сбрасываем черновик и ошибку. */
  roomId: string;
  /** Участники комнаты для автодополнения @. Уже отфильтрованы/отсортированы. */
  members: MemberInfo[];
  /** Могу ли упомянуть всю комнату (@room). */
  canMentionRoom: boolean;
  onSend: (segments: RichSegment[]) => Promise<void>;
  /** Отправка файла с прогрессом. Восстановлено: правка 14.09 затёрла строку. */
  onSendFile?: (file: File, caption: string | undefined, onProgress: (percent: number) => void) => Promise<void>;
  /** Отправка голосового сообщения. */
  onSendVoice?: (file: File, duration: number, waveform: number[]) => Promise<void>;
  /** Если задан — композер в режиме ответа на сообщение. */
  replyTo?: {
    eventId: string;
    senderName: string;
    snippet: string;
    /** mxc миниатюры для цитаты-картинки/видео (Антон 2026-09-14). */
    thumbMxc?: string | null;
  } | null;
  onCancelReply?: () => void;
  /** Если задан — композер в режиме редактирования сообщения. */
  editing?: { eventId: string; originalBody: string } | null;
  onCancelEdit?: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const client = useMatrixClient();
  /** Ключ черновика этой комнаты в localStorage (alg.draft.<roomId>). */
  const storageKey = draftKey(roomId);
  const [text, setText] = useState("");
  const [formats, setFormats] = useState<DraftFormat[]>([]);
  /** Marks applied to future input while the textarea selection is collapsed. */
  const [activeMarks, setActiveMarks] = useState<TextMark[]>([]);
  // While an IME composition is active, let the native textarea paint its
  // composition text/underline; the formatted mirror resumes on compositionend.
  const [composing, setComposing] = useState(false);
  const [mentions, setMentions] = useState<DraftMention[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** null — загрузки нет; число — проценты. */
  const [uploading, setUploading] = useState<number | null>(null);
  /** Files stay local until Enter/the send button confirms the attachment. */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /** Открытое автодополнение @: позиция начала и текущий запрос. */
  const [mention, setMention] = useState<{
    start: number;
    query: string;
    activeIndex: number;
  } | null>(null);
  /** Координаты открытого меню вложений (плюсик); null — закрыто. */
  const [attachMenuAt, setAttachMenuAt] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const pendingInput = useRef<{ start: number; end: number; inputType: string } | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** Корень композера: нужен, чтобы отличить клик внутри от клика снаружи. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Над окном тащат файл — показываем плашку «отпустите». */
  const [dragging, setDragging] = useState(false);
  /** Координаты открытого пикера эмодзи; null — закрыт. */
  const [emojiAt, setEmojiAt] = useState<{ x: number; y: number } | null>(null);
  /** Recording is kept locally after stop; upload only happens on Send. */
  const [voice, setVoice] = useState<{
    phase: "idle" | "recording" | "ready" | "sending";
    seconds: number;
    error: string | null;
    levels: number[];
  }>({ phase: "idle", seconds: 0, error: null, levels: [] });
  const voiceTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingVoice = useRef<{ file: File; duration: number; waveform: number[] } | null>(null);

  /**
   * Активная сессия записи — mutable, НЕ в React-состоянии. `onstop` читает
   * всё отсюда, а не из замыкания на устаревший `voice`: длительность берём
   * по реальному времени (startAt), waveform — по снятым RMS-замерам.
   */
  const voiceSession = useRef<{
    stream: MediaStream;
    recorder: MediaRecorder;
    audioCtx: AudioContext | null;
    chunks: Blob[];
    mime: string;
    startAt: number;
    raf: number | null;
  } | null>(null);
  /** Снятые RMS-замеры громкости по ходу записи (потом сжимаем в 30 корзин). */
  const rmsSamples = useRef<number[]>([]);

  const releaseVoiceSession = () => {
    if (voiceTimer.current) clearInterval(voiceTimer.current);
    voiceTimer.current = null;
    const s = voiceSession.current;
    voiceSession.current = null;
    rmsSamples.current = [];
    if (!s) return;
    if (s.raf !== null) cancelAnimationFrame(s.raf);
    try {
      if (s.recorder.state !== "inactive") s.recorder.stop();
    } catch {
      /* recorder мог быть уже остановлен */
    }
    s.stream.getTracks().forEach((t) => t.stop());
    if (s.audioCtx) void s.audioCtx.close().catch(() => {});
  };

  const discardVoice = () => {
    releaseVoiceSession();
    pendingVoice.current = null;
    setVoice({ phase: "idle", seconds: 0, error: null, levels: [] });
  };

  // Сброс голосовой записи при смене комнаты или unmount.
  useEffect(() => {
    return () => {
      discardVoice();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  /** Stop only prepares a preview; it never uploads automatically. */
  const stopVoice = () => {
    const session = voiceSession.current;
    if (!session) return;
    if (session.recorder.state !== "inactive") session.recorder.stop();
  };

  const startVoice = async () => {
    setVoice((v) => ({ ...v, error: null }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Safari/macOS supports MP4/AAC more reliably than WebM/Opus.
      const mimeCandidates = [
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
      ];
      const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
      if (!mime) {
        stream.getTracks().forEach((t) => t.stop());
        setVoice((v) => ({ ...v, error: t("ui.brauzer_ne_umeet_zapisyvat_opus") }));
        return;
      }

      const startAt = performance.now();
      const chunks: Blob[] = [];
      rmsSamples.current = [];

      // AnalyserNode: снимаем реальную громкость (RMS) для формы волны.
      let audioCtx: AudioContext | null = null;
      let analyserTick: FrameRequestCallback | null = null;
      try {
        const AudioCtx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          audioCtx = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);
          let lastPaint = 0;
          const tick = (now: number) => {
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
              const v = (data[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / data.length);
            rmsSamples.current.push(rms);
            // ~20fps and at most 40 bars keeps React work bounded.
            if (now - lastPaint >= 50) {
              lastPaint = now;
              setVoice((v) => ({ ...v, levels: [...v.levels.slice(-39), Math.max(3, Math.min(100, Math.round(rms * 320)))] }));
            }
            const active = voiceSession.current;
            if (active) active.raf = requestAnimationFrame(tick);
          };
          analyserTick = tick;
          // Contexts can start suspended on WebKit; getUserMedia was caused by
          // a user gesture, so resume here while that activation is warm.
          if (ctx.state === "suspended") await ctx.resume();
        }
      } catch {
        // Analyser не критичен: без него waveform останется из нулевых RMS.
      }

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      voiceSession.current = { stream, recorder, audioCtx, chunks, mime, startAt, raf: null };
      if (analyserTick) {
        // Start analyser only after the session ref exists.
        voiceSession.current.raf = requestAnimationFrame(analyserTick);
      }
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = async () => {
        const session = voiceSession.current;
        const samples = rmsSamples.current.slice();
        if (!session) return;
        const sessionStartAt = session.startAt;
        const blob = new Blob(chunks, { type: mime });
        const duration = (performance.now() - sessionStartAt) / 1000;
        releaseVoiceSession();
        if (duration < 1) {
          setVoice({ phase: "idle", seconds: 0, levels: [], error: t("ui.slishkom_korotko") });
          return;
        }
        if (duration > 300) {
          setVoice({ phase: "idle", seconds: 0, levels: [], error: t("ui.golosovoe_dlinnee_5_minut") });
          return;
        }
        // Waveform: 30 уровней 0..100 из реального RMS (MSC1767).
        const waveform = Array.from({ length: 30 }, (_, b) => {
          const from = Math.floor((b * samples.length) / 30);
          const to = Math.floor(((b + 1) * samples.length) / 30);
          let sum = 0;
          let n = 0;
          for (let i = from; i < to; i++) {
            sum += samples[i];
            n++;
          }
          if (n === 0) return 0;
          const avg = sum / n;
          return Math.min(100, Math.max(0, Math.round(avg * 280)));
        });
        const extension = mime.startsWith("audio/mp4") ? "m4a" : mime.startsWith("audio/webm") ? "webm" : "ogg";
        const file = new File([blob], `voice-${Date.now()}.${extension}`, { type: mime });
        pendingVoice.current = { file, duration, waveform };
        setVoice((v) => ({ ...v, phase: "ready", seconds: Math.round(duration), levels: waveform }));
      };

      recorder.start(1000);
      voiceTimer.current = setInterval(() => {
        setVoice((v) => ({ ...v, seconds: v.seconds + 1 }));
      }, 1000);
      setVoice({ phase: "recording", seconds: 0, error: null, levels: [] });
    } catch (e) {
      releaseVoiceSession();
      setVoice((v) => ({
        ...v,
        phase: "idle",
        error: (e as Error).name === "NotAllowedError"
          ? t("ui.dostup_k_mikrofonu_zapresch_n")
          : tfmt("composer.micEnableFailed", { error: (e as Error).message }),
      }));
    }
  };

  const sendVoice = async () => {
    const pending = pendingVoice.current;
    if (!pending || !onSendVoice || voice.phase === "sending") return;
    setVoice((v) => ({ ...v, phase: "sending", error: null }));
    try {
      await onSendVoice(pending.file, pending.duration, pending.waveform);
      discardVoice();
    } catch (e) {
      setVoice((v) => ({ ...v, phase: "ready", error: (e as Error).message }));
    }
  };

  const cancelVoice = () => {
    discardVoice();
  };
  /**
   * Счётчик вложенности dragenter/dragleave.
   *
   * ЗАМЕРЕННАЯ ГРАБЛЯ (она же в плане): события всплывают, и при проходе
   * курсора над дочерними элементами приходит dragleave родителя ДО
   * dragenter ребёнка. Простой булев флаг из-за этого мигает. Считаем
   * входы/выходы: плашка гаснет только когда счётчик вернулся в ноль.
   */
  const dragDepth = useRef(0);

  useEffect(() => {
    // ЧЕРНОВИК ПО КОМНАТЕ (Антон, 2026-09-16 21:26 UTC): при входе в
    // комнату восстанавливаем набранное, а не стираем его. Раньше здесь
    // было setText("") — переключение комнаты безвозвратно убивало текст.
    setText(loadDraft(storageKey));
    setFormats([]);
    setActiveMarks([]);
    setMentions([]);
    setMention(null);
    setError(null);
    setPendingFiles([]);

    // Desktop keeps the convenient ready-to-type focus. On touch devices
    // (notably iPad Safari in desktop-site mode), focusing while a room is
    // mounted opens the software keyboard without a user gesture and cuts
    // the usable chat viewport roughly in half. Let the user's first tap on
    // the composer open the keyboard instead.
    const touchPrimary =
      (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
      navigator.maxTouchPoints > 0;
    if (!touchPrimary) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Сохранение черновика с debounce 300 мс (постановка). Ловит ЛЮБОЕ
  // изменение text — и ручной ввод, и вставку эмодзи, и кнопки разметки.
  // В РЕЖИМЕ ПРАВКИ не сохраняем: текст поля тогда — оригинал чужого
  // сообщения, и затирание им черновика потеряло бы набранное раньше
  // (правка и черновик живут раздельно, как в Element).
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Последний text для синхронного сброса при смене комнаты/unmount. */
  const latestText = useRef(text);
  latestText.current = text;
  /** Свежий флаг правки для cleanup-эффекта (тот же приём, что и text). */
  const editingRef = useRef(editing);
  editingRef.current = editing;
  useEffect(() => {
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    if (editing) return;
    draftSaveTimer.current = setTimeout(() => {
      draftSaveTimer.current = null;
      saveDraft(storageKey, text);
    }, 300);
    return () => {
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, text, editing]);

  // Cleanup debounce-эффекта ОТМЕНЯЕТ ожидавший таймер: последние <300 мс
  // ввода при переключении комнаты или перезагрузке страницы потерялись бы.
  // Дописываем черновик синхронно при смене ключа/размонтировании.
  useEffect(() => {
    return () => {
      if (!editingRef.current) saveDraft(storageKey, latestText.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Индикатор набора: «печатаю» не чаще раза в ~10 с, гаснет при отправке
  // и при очистке поля (см. matrix/typing.ts).
  const typing = useTypingSender(client, roomId);

  // При входе в режим редактирования подставляем исходный текст.
  useEffect(() => {
    setActiveMarks([]);
    if (editing) {
      // Перед подменой поля текстом чужого сообщения дописываем черновик
      // синхронно: debounce мог ещё не успеть (<300 мс с последнего
      // символа), а сам текст правки черновиком НЕ является.
      saveDraft(storageKey, latestText.current);
      // Editing a text event and composing a new attachment are mutually
      // exclusive; never let Enter accidentally upload a stale draft here.
      setPendingFiles([]);
      setText(editing.originalBody);
      setFormats([]);
      ref.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // При входе в режим ОТВЕТА фокус сразу уходит в поле ввода — человек
  // жмёт «Ответить» и печатает без лишнего клика (Антон, 2026-09-14).
  // Эффект, а не обработчик клика: ждём рендер плашки, иначе фокус слетит.
  // Каретку ставим В КОНЕЦ уже набранного текста, не выделяя его.
  useEffect(() => {
    if (!replyTo) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [replyTo]);

  // Клик МИМО списка закрывает автодополнение (Антон, 2026-09-10 11:24).
  // Раньше список жил, пока не сотрёшь «@» — приходилось удалять символ
  // руками. Текст НЕ трогаем: закрываем только список, набранное остаётся.
  //
  // pointerdown, а не click: срабатывает раньше и не конфликтует с
  // onMouseDown выбора пункта (там preventDefault держит фокус textarea).
  useEffect(() => {
    if (!mention) return;
    const onOutside = (e: PointerEvent) => {
      const root = rootRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setMention(null);
      }
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, [mention]);

  // Клик мимо меню вложений (плюсик) закрывает его. Та же логика, что и у
  // автодополнения: pointerdown раньше click, чтобы не рушить выбор.
  useEffect(() => {
    if (!attachMenuAt) return;
    const onOutside = (e: PointerEvent) => {
      const root = rootRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setAttachMenuAt(null);
      }
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, [attachMenuAt]);

  /** Каретка после выбора упоминания — ставится после коммита `text`. */
  const caretAfterCommit = useCaretAfterCommit(ref, text);

  // Автовысота: textarea растёт до maxHeight из стилей, потом скроллится.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  // Кандидаты в автодополнении: фильтр по запросу, @room — если есть право.
  const candidates = useMemo(() => {
    const q = mention?.query.toLowerCase() ?? "";
    const list: {
      userId: string | null;
      displayName: string;
      name: string;
      /** MXID в скобках после имени; пусто, когда имя и есть MXID. */
      subtitle: string;
      invited: boolean;
    }[] = [];

    if (canMentionRoom && "room".startsWith(q)) {
      list.push({
        userId: null,
        displayName: "@room",
        name: t("composer.mentionAll"),
        subtitle: "",
        invited: false,
      });
    }
    for (const mm of members) {
      // Себя в списке оставляем (как в Element): упоминание себя — валидный
      // кейс (self-note, напоминание), а отдельный фильтр здесь только
      // добавил бы ветку без выигрыша.
      const name = mm.name;
      const hay = `${name} ${mm.userId}`.toLowerCase();
      if (hay.includes(q)) {
        // Формат: «@Имя (@login:server)» — имя первым, адрес в скобках
        // (Антон, 2026-09-10 11:28). Если имени нет, membersOf подставляет
        // в `name` сам MXID — тогда скобки с тем же текстом были бы
        // масляным маслом, и подпись остаётся пустой.
        const hasName = name !== mm.userId;
        list.push({
          userId: mm.userId,
          displayName: hasName ? `@${name}` : mm.userId,
          name,
          subtitle: hasName ? `(${mm.userId})` : "",
          // Приглашённый ещё не в комнате: упомянуть можно, но пуш он
          // получит только после входа — честнее сказать это сразу.
          invited: mm.membership === "invite",
        });
      }
    }
    // Вошедшие выше приглашённых: сначала те, кто реально прочитает.
    list.sort((a, b) => Number(a.invited) - Number(b.invited));
    return list.slice(0, 8);
  }, [mention?.query, members, canMentionRoom]);

  /**
   * Обработка ввода: если прямо перед курсором стоит `@…`, открываем список.
   * Запрос — текст между `@` и курсором; список фильтруется на лету.
   */
  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Человек печатает дальше — отложенная каретка упоминания больше не нужна.
    caretAfterCommit.cancel();
    const value = e.target.value;
    // Keep ranges stable for edits wholly before/after them; if an edit
    // intersects a formatted range, drop that range rather than formatting
    // text the user did not select.
    let prefix = 0;
    while (prefix < text.length && prefix < value.length && text[prefix] === value[prefix]) prefix++;
    let suffix = 0;
    while (suffix < text.length - prefix && suffix < value.length - prefix && text[text.length - 1 - suffix] === value[value.length - 1 - suffix]) suffix++;
    let editStart = prefix;
    let oldEnd = text.length - suffix;
    let newEnd = value.length - suffix;
    const delta = value.length - text.length;
    const beforeInput = pendingInput.current;
    pendingInput.current = null;
    const inputType = (e.nativeEvent as InputEvent).inputType || beforeInput?.inputType || "";
    // A string diff is ambiguous when the inserted text matches its
    // neighbours (typing "a" before another "a"). beforeinput gives us the
    // actual replaced selection; retain the diff as a fallback for tests and
    // browser input paths that do not expose it.
    if (beforeInput?.inputType.startsWith("insert")) {
      const insertedLength = value.length - (text.length - (beforeInput.end - beforeInput.start));
      if (insertedLength >= 0) {
        editStart = beforeInput.start;
        oldEnd = beforeInput.end;
        newEnd = editStart + insertedLength;
      }
    }
    const applyTypingMarks = newEnd > editStart && (!inputType || inputType.startsWith("insert"));
    setFormats((prev) => prev.flatMap((f) => {
      if (f.end <= editStart) return [f];
      if (f.start >= oldEnd) return [{ ...f, start: f.start + delta, end: f.end + delta }];
      return [];
    }).concat(applyTypingMarks
      ? activeMarks.map((mark) => ({ start: editStart, end: newEnd, mark }))
      : []));
    setText(value);
    // Индикатор набора у собеседников: ввод = «печатаю» (троттлинг внутри),
    // очищенное поле = typing:false.
    typing.noteText(value);

    const caret = e.target.selectionStart ?? value.length;
    // Ищем ближайший `@`, от которого до курсора нет пробелов/переносов.
    const lastBreak = Math.max(
      value.lastIndexOf(" ", caret - 1),
      value.lastIndexOf("\n", caret - 1),
    );
    const at = value.lastIndexOf("@", caret - 1);
    // `@` должен быть началом «слова» (после пробела/начала строки).
    if (at > lastBreak) {
      const query = value.slice(at + 1, caret);
      // Не открываем посреди набора, если после @ уже идёт ввод, но запрос
      // должен быть «словом» без пробелов.
      if (!query.includes(" ") && !query.includes("\n")) {
        setMention({ start: at, query, activeIndex: 0 });
        return;
      }
    }
    setMention(null);
  };

  const toggleFormat = (mark: TextMark) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    if (end <= start) {
      setActiveMarks((prev) => {
        const exists = prev.some((active) => sameMark(active, mark));
        if (typeof mark === "object") {
          // Future typing has at most one color. Clicking that color again
          // removes it; choosing another color replaces it.
          const withoutColor = prev.filter((active) => typeof active !== "object");
          return exists ? withoutColor : [...withoutColor, mark];
        }
        return exists
          ? prev.filter((active) => !sameMark(active, mark))
          : [...prev, mark];
      });
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start, end);
      });
      return;
    }
    setFormats((prev) => {
      const exists = prev.some((f) => f.start === start && f.end === end && sameMark(f.mark, mark));
      return exists
        ? prev.filter((f) => !(f.start === start && f.end === end && sameMark(f.mark, mark)))
        : [...prev, { start, end, mark }];
    });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, end);
    });
  };

  /**
   * Заменить участок черновика текстом ПРОГРАММНО (не через onChange).
   *
   * Смещения диапазонов форматирования пересчитываются тем же правилом,
   * что и в onChange: диапазон целиком до правки — не трогаем, целиком
   * после — сдвигаем, пересекающийся — снимаем (лучше потерять жирный
   * шрифт, чем применить его к тексту, который никто не выделял).
   */
  const replaceRange = (start: number, end: number, insert: string) => {
    const next = text.slice(0, start) + insert + text.slice(end);
    const delta = insert.length - (end - start);
    setFormats((prev) =>
      prev.flatMap((f) => {
        if (f.end <= start) return [f];
        if (f.start >= end) return [{ ...f, start: f.start + delta, end: f.end + delta }];
        return [];
      }),
    );
    // КАРЕТКА СИНХРОННО, А НЕ В requestAnimationFrame (Антон 19.10 07:53,
    // «last row disappeared»). Раньше value менялся через setText, а каретка
    // ставилась на следующем кадре. Буквы, набранные сразу после Shift+Enter
    // (до этого кадра), попадали в старую позицию каретки, т.е. в КОНЕЦ
    // ПРЕДЫДУЩЕГО пункта, и потом rAF прыгал на новый пункт. В итоге текст
    // последней строки оказывался приклеен к пункту выше, а «8.» оставался
    // пустым. flushSync коммитит value прямо в обработчике keydown, и
    // каретка встаёт до того, как браузер обработает следующую клавишу.
    flushSync(() => {
      setText(next);
    });
    // Тот же побочный эффект, что у ручного ввода: «печатает…» у собеседников
    // и закрытие автодополнения — иначе список участников остался бы висеть.
    typing.noteText(next);
    setMention(null);
    const caret = start + insert.length;
    const el = ref.current;
    if (el) {
      if (document.activeElement !== el) el.focus();
      el.setSelectionRange(caret, caret);
    }
  };

  /** Подтвердить выбор участника из списка — вставить имя и закрыть список. */
  //
  // Каретка — В КОНЕЦ имени + ровно один пробел (Антон, 2026-10-16 09:50,
  // «на iOS и Mac одинаково»). Раньше конец заменяемого «@запроса» брался из
  // selectionStart, а каретка ставилась одним rAF из обработчика — в WKWebView
  // Mac-оболочки она оставалась посреди имени. Теперь граница считается из
  // состояния, а каретка ставится после коммита (см. mentionInsert.ts).
  const pickMention = (c: { userId: string | null; displayName: string }) => {
    if (!mention) return;
    const r = insertMention(text, mention.start, mention.query, c.displayName);
    // Диапазоны форматирования сдвигаем тем же правилом, что replaceRange.
    setFormats((prev) =>
      prev.flatMap((f) => {
        if (f.end <= r.replaceStart) return [f];
        if (f.start >= r.replaceEnd) return [{ ...f, start: f.start + r.delta, end: f.end + r.delta }];
        return [];
      }),
    );
    setText(r.text);
    typing.noteText(r.text);
    setMentions((prev) => [
      ...prev,
      { userId: c.userId, displayName: c.displayName, length: c.displayName.length },
    ]);
    setMention(null);
    caretAfterCommit.request(r.caret);
  };

  /** Клавиатурная навигация по списку автодополнения и отмена. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && candidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMention((m) => (m ? { ...m, activeIndex: (m.activeIndex + 1) % candidates.length } : m));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMention((m) =>
          m ? { ...m, activeIndex: (m.activeIndex - 1 + candidates.length) % candidates.length } : m,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(candidates[mention.activeIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }

    // Enter ОТПРАВЛЯЕТ сообщение только на десктопе (Антон 05.10):
    // на тач-устройствах (телефон/планшет) чистый Enter — перенос строки,
    // отправка ТОЛЬКО кнопкой-стрелкой (как в iOS). На десктопе — отправка.
    // Признак тач-устройства: pointer:coarse или maxTouchPoints>0 — уже
    // используется ниже для фокуса при входе в комнату (та же логика).
    const touchPrimary =
      (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
      navigator.maxTouchPoints > 0;
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !touchPrimary) {
      e.preventDefault();
      void submit();
      return;
    }

    // На тач-устройстве Enter — перенос строки: preventDefault НЕТ,
    // браузер сам вставит \n, иначе перенос в поле не попадал бы вообще
    // (та же вторая беда, что была в iOS: перехватчик глотал \n).

    // Shift+Enter ПРОДОЛЖАЕТ СПИСОК (Антон 2026-09-18, п.5).
    //
    // Только чистый Shift+Enter: с Ctrl/Alt/Meta это уже другое сочетание,
    // перехватывать его мы не вправе. И только при СХЛОПНУТОМ курсоре:
    // при выделении Shift+Enter заменяет выделенный текст переносом —
    // штатное поведение поля ввода, ломать его незачем.
    if (e.key === "Enter" && e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const el = ref.current;
      if (!el) return;
      const caret = el.selectionStart ?? 0;
      const selEnd = el.selectionEnd ?? caret;
      if (caret !== selEnd) return;
      // Начало строки. При caret === 0 lastIndexOf с fromIndex -1 всё равно
      // смотрит на позицию 0 и может найти там \n — отсюда явная ветка.
      const lineStart = caret === 0 ? 0 : text.lastIndexOf("\n", caret - 1) + 1;
      const action = listContinuationFor(text.slice(lineStart, caret));
      if (!action) return; // не список — обычный перенос строки от браузера
      e.preventDefault();
      if (action.type === "continue") {
        replaceRange(caret, caret, action.insert);
      } else {
        // Выход из списка: стираем только пустой маркер, НЕ добавляя
        // переноса: человек остаётся на той же (теперь пустой) строке и
        // пишет обычный абзац. Лишняя пустая строка в сообщении была бы
        // видна всем в ленте, а просили только «выйти из списка».
        replaceRange(caret - action.removeChars, caret, "");
      }
    }
  };

  const submit = async () => {
    if (busy) return;
    if (pendingFiles.length > 0) {
      await sendPendingAttachments();
      return;
    }
    if (!hasContent(text, mentions)) return;
    const segments = buildSegments(text, mentions, formats);
    // Поле чистим сразу: локальное эхо уже покажет сообщение в ленте.
    setText("");
    setFormats([]);
    setActiveMarks([]);
    setMentions([]);
    setMention(null);
    setError(null);
    setBusy(true);
    try {
      await onSend(segments);
      // Успешная отправка: черновик отработал — удаляем из localStorage,
      // иначе при следующем входе в комнату поле «воскресит» уже
      // отправленное сообщение (Антон, 2026-09-16 21:26 UTC).
      // ПРАВКА сообщения черновик НЕ трогает: он хранит незаконченную
      // мысль и обязан пережить редактирование чужого текста.
      if (!editing) clearDraft(storageKey);
      // Собеседникам гасим «печатает…» сразу, не дожидаясь серверного
      // таймаута в 30 с.
      typing.stop();
      // Успешная отправка в режиме редактирования/ответа — выходим из режима.
      if (editing) onCancelEdit?.();
      if (replyTo) onCancelReply?.();
    } catch (e) {
      setError((e as Error).message);
      setText(text);
      setFormats(formats);
      setActiveMarks(activeMarks);
      setMentions(mentions);
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  /**
   * Выбор файла. Скрытый <input type=file> + кнопка-скрепка. После выбора
   * обнуляем value — иначе повторный выбор ТОГО ЖЕ файла не вызовет change.
   * Accept тоже сбрасываем: его ставит меню вложений на время клика,
   * но drag&drop и вставка из буфера идут через тот же input и не должны
   * унаследовать ограничение «только фото/видео».
  */
  const stageFiles = (files: File[]) => {
    if (!onSendFile || disabled || editing || busy || files.length === 0) return;
    setError(null);
    setPendingFiles((current) => [...current, ...files]);
    if (fileRef.current) {
      // Allow choosing the same file again and do not leak an accept filter
      // from the photo/video menu into the next selection.
      fileRef.current.value = "";
      fileRef.current.accept = "";
    }
    requestAnimationFrame(() => ref.current?.focus());
  };

  /**
   * Открыть выбор файла для вложений.
   *
   * В Mac-оболочке системное окно не открывается штатным `input.click()`
   * (замер Антона 24.09: сразу «файл не выбран», Finder не появляется),
   * поэтому там окно открывает сама оболочка через мост `window.algShell`,
   * а результат приходит обычными `File` в тот же `stageFiles`.
   *
   * В браузере моста нет и выполняется ровно прежний код: выставить
   * `accept` и синхронно нажать на скрытый input.
   */
  const openAttachPicker = (kind: "media" | "file") => {
    if (isShellFilePickerAvailable()) {
      void (async () => {
        try {
          const files = await pickFilesViaShell({ kind, multiple: true });
          // null — человек закрыл окно, ничего не выбрав. Молча.
          if (files && files.length > 0) stageFiles(files);
        } catch (err) {
          setError(shellPickErrorText(err));
        }
      })();
      return;
    }

    // accept для фото/видео ставим только на время клика: drag&drop и
    // вставка из буфера идут через тот же input, но им accept не должен
    // мешать. После выбора accept сбрасывается обратно в stageFiles.
    if (fileRef.current) {
      fileRef.current.accept = kind === "media" ? "image/*,video/*" : "";
      fileRef.current.click();
    }
  };

  /**
   * Send pending files sequentially. A failure leaves that file and all
   * following files in the preview, while already-sent files are removed.
   * Строго последовательно: параллельная загрузка упёрлась бы в rc_message
   * (тот же лимит, что и на отправку сообщений) и часть файлов отвалилась бы
   * с 429. Первая ошибка останавливает пачку — иначе человек увидит одну
   * ошибку и не поймёт, сколько из десяти файлов реально ушло.
   */
  async function sendPendingAttachments() {
    if (!onSendFile || busy || pendingFiles.length === 0) return;
    const files = pendingFiles;
    const caption = text.trim().length > 0 ? text : undefined;
    setError(null);
    setBusy(true);
    setUploading(0);
    let sent = 0;
    try {
      for (const file of files) {
        await onSendFile(file, caption, (p) => setUploading(p));
        sent += 1;
        setPendingFiles(files.slice(sent));
        setUploading(0);
      }
      setText("");
      setFormats([]);
      setActiveMarks([]);
      setMentions([]);
      setMention(null);
      // Подпись ушла вместе с файлами: черновик отработан, «печатает…» гасим.
      clearDraft(storageKey);
      typing.stop();
    } catch (e) {
      setError((e as Error).message);
      setPendingFiles(files.slice(sent));
    } finally {
      setBusy(false);
      setUploading(null);
      ref.current?.focus();
    }
  }

  // Перетаскивание файла В ОКНО (Антон, 2026-09-10 11:34). Слушатели на
  // document, а не на композере: люди бросают файл «в чат», то есть в любое
  // место окна, а не точно в поле ввода.
  useEffect(() => {
    if (!onSendFile || disabled || editing) return;

    // Есть ли в переносе именно файлы. Перетаскивание выделенного ТЕКСТА
    // тоже стреляет dragover — плашку на него показывать нельзя.
    const hasFiles = (dt: DataTransfer | null) =>
      !!dt && Array.from(dt.types).includes("Files");

    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      dragDepth.current += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      // Без preventDefault браузер откроет файл как страницу вместо drop.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) stageFiles(files);
    };

    document.addEventListener("dragenter", onEnter);
    document.addEventListener("dragover", onOver);
    document.addEventListener("dragleave", onLeave);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragenter", onEnter);
      document.removeEventListener("dragover", onOver);
      document.removeEventListener("dragleave", onLeave);
      document.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSendFile, roomId, disabled, editing, busy]);

  const sendable = pendingFiles.length > 0 || hasContent(text, mentions);
  // Reuse the exact range splitting used for sending, but omit mentions: this
  // layer previews formatting only and never changes the RichSegment payload.
  const previewSegments = useMemo(() => buildSegments(text, [], formats), [text, formats]);

  return (
    <div style={{ flex: "0 0 auto" }} ref={rootRef}>
      {/* Стили композера: hover и focus-visible inline-стилем не выразить. */}
      <style>{composerCss}</style>
      {/* Пилюли подтверждённых упоминаний — видимый ответ «кого я упомяну».
          Набрать @Имя вручную можно, но пилюля и push появятся только после
          выбора из списка. */}
      {mentions.length > 0 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 14px 0" }}
          data-testid="mention-pills"
        >
          {mentions.map((m, i) => (
            <span
              key={`${m.userId ?? "room"}-${i}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: radius.pill,
                background: color.accentSoft,
                color: color.accent,
                fontSize: font.tiny,
              }}
            >
              {m.displayName}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div style={{ padding: "6px 14px", fontSize: 12, color: palette.danger }} role="alert">
          {tfmt("composer.sendError", { error: error ?? "" })}
        </div>
      )}

      {dragging && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(20,24,31,0.45)",
            // Плашка НЕ должна перехватывать сам drop: события уже висят на
            // document, а pointerEvents:none снимает риск, что оверлей
            // окажется целью dragleave и вызовет мигание.
            pointerEvents: "none",
          }}
          data-testid="drop-overlay"
        >
          <div
            style={{
              padding: "18px 28px",
              borderRadius: radius.md,
              background: color.bg,
              color: color.text,
              fontWeight: font.weightBold,
              border: `2px dashed ${color.accent}`,
              boxShadow: "0 8px 32px rgba(20,24,31,0.28)",
            }}
          >{t("composer.releaseToSend")}</div>
        </div>
      )}

      {uploading !== null && (
        <div
          style={{ padding: "6px 14px", fontSize: 12, color: palette.textDim }}
          data-testid="upload-progress"
        >
          {tfmt("composer.fileUploadProgress", { percent: uploading })}
        </div>
      )}

      {voice.error && (
        <div style={{ padding: "6px 14px", fontSize: 12, color: palette.danger }} role="alert">
          {voice.error}
        </div>
      )}

      {voice.phase !== "idle" && (
        <div className="alg-composer-voice" data-testid="voice-recording">
          <button
            type="button"
            className="alg-composer-btn"
            onClick={cancelVoice}
            disabled={voice.phase === "sending"}
            data-testid="voice-cancel"
            title={t("composer.deleteRecording")}
            aria-label={t("composer.deleteRecording")}
          ><IconTrash size={19} /></button>
          <div className="alg-voice-track" aria-label={tfmt("composer.recordingDuration", { seconds: voice.seconds })}>
            <span className={voice.phase === "recording" ? "alg-voice-dot active" : "alg-voice-dot"} />
            <div className="alg-voice-wave" aria-hidden="true">
              {(voice.levels.length ? voice.levels : Array(20).fill(8)).map((level, i) => (
                <i key={i} style={{ height: `${Math.max(3, Math.min(100, level))}%` }} />
              ))}
            </div>
            <span className="alg-voice-duration">{voice.seconds}{t("common.secShort")}</span>
          </div>
          {voice.phase === "recording" ? (
            <button type="button" className="alg-composer-btn send" onClick={stopVoice} data-testid="voice-stop" title={t("composer.stopRecording")} aria-label={t("composer.stopRecording")}>
              <IconStop size={18} />
            </button>
          ) : (
            <button type="button" className="alg-composer-btn send" onClick={() => void sendVoice()} disabled={voice.phase === "sending"} data-testid="voice-send" title={t("composer.sendButton")}>
              <IconSend size={20} />
            </button>
          )}
        </div>
      )}

      {/* Выпадающий список автодополнения @. Позиционируется сразу над композером. */}
      {mention && candidates.length > 0 && (
        <div
          style={{
            margin: "0 14px",
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            background: color.bg,
            boxShadow: "0 4px 16px rgba(20,24,31,0.14)",
            maxHeight: 240,
            overflowY: "auto",
          }}
          data-testid="mention-list"
        >
          {candidates.map((c, i) => (
            <button
              key={c.displayName}
              type="button"
              onMouseDown={(e) => {
                // mouseDown раньше blur textarea — иначе список закроется до клика.
                e.preventDefault();
                pickMention(c);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: space.md,
                width: "100%",
                padding: "8px 12px",
                border: "none",
                background: i === mention.activeIndex ? color.accentSoft : "transparent",
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
              }}
              data-testid={`mention-item-${c.userId ?? "room"}`}
            >
              <span
                style={{
                  minWidth: 0,
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    fontWeight: font.weightBold,
                    color: color.text,
                    flex: "0 1 auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.displayName}
                </span>
                {c.subtitle && (
                  <span
                    style={{
                      fontSize: font.tiny,
                      color: palette.textDim,
                      flex: "0 1 auto",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.subtitle}
                  </span>
                )}
              </span>
              {c.invited && (
                <span
                  style={{ fontSize: font.tiny, color: palette.textDim, flex: "0 0 auto" }}
                  title={t("ui.priglash_n_no_esch_ne_vosh_l_uvedomlenie_pri")}
                >{t("composer.mentionInvited")}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* БАННЕРЫ ответа и редактирования — НАД рядом кнопок, а не внутри
          него: иначе баннер оказывается flex-элементом рядом с кнопками
          и полем ввода и ломает линейку. */}
      {replyTo && (
        <div className="alg-composer-banner" data-testid="reply-banner">
          <div className="alg-composer-banner-body" style={{ display: "flex", alignItems: "center", gap: space.md }}>
            {replyTo.thumbMxc && (
              <ComposerThumb mxc={replyTo.thumbMxc} />
            )}
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div className="alg-composer-banner-title">{tfmt("composer.reply", { user: replyTo.senderName })}</div>
              <div className="alg-composer-banner-snippet">{replyTo.snippet}</div>
            </div>
          </div>
          <button
            type="button"
            className="alg-composer-banner-close"
            onClick={onCancelReply}
            data-testid="reply-cancel"
            title={t("composer.cancelReply")}
          >
            <IconClose size={16} />
          </button>
        </div>
      )}
      {editing && (
        <div className="alg-composer-banner editing" data-testid="edit-banner">
          <div className="alg-composer-banner-body">
            <div className="alg-composer-banner-title">{t("composer.editing")}</div>
            <div className="alg-composer-banner-snippet">
              {editing.originalBody.slice(0, 60)}
            </div>
          </div>
          <button
            type="button"
            className="alg-composer-banner-close"
            onClick={onCancelEdit}
            data-testid="edit-cancel"
            title={t("composer.cancelEdit")}
          >
            <IconClose size={16} />
          </button>
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="alg-pending-attachments" data-testid="pending-attachments">
          {pendingFiles.map((file, index) => (
            <div className="alg-pending-attachment" key={`${file.name}-${file.size}-${index}`}>
              {file.type.startsWith("image/") ? (
                <PendingImagePreview file={file} />
              ) : (
                <span className="alg-pending-attachment-file" aria-hidden="true"><IconFile size={24} /></span>
              )}
              <div className="alg-pending-attachment-meta">
                <span className="alg-pending-attachment-name" title={file.name}>{file.name}</span>
                <span className="alg-pending-attachment-size">{formatBytes(file.size)}</span>
              </div>
              <button
                type="button"
                className="alg-composer-banner-close"
                onClick={() => setPendingFiles((files) => files.filter((_, i) => i !== index))}
                disabled={busy}
                title={t("common.cancel")}
                aria-label={t("common.cancel")}
                data-testid={`pending-attachment-remove-${index}`}
              >
                <IconClose size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {voice.phase === "idle" && (
        <div className="alg-format-toolbar" role="toolbar" aria-label={t("composer.formattingToolbar")}>
          {([
            ["bold", "B", "composer.bold"],
            ["italic", "I", "composer.italic"],
            ["underline", "U", "composer.underline"],
            ["strikethrough", "S", "composer.strikethrough"],
          ] as const).map(([mark, label, titleKey]) => (
            <button
              key={mark}
              type="button"
              className={`alg-format-btn ${mark}${activeMarks.some((active) => sameMark(active, mark)) ? " active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleFormat(mark)}
              title={t(titleKey)}
              aria-label={t(titleKey)}
              aria-pressed={activeMarks.some((active) => sameMark(active, mark))}
            >{label}</button>
          ))}
          <span className="alg-format-divider" />
          {([
            ["red", "composer.textColorRed"],
            ["orange", "composer.textColorOrange"],
            ["green", "composer.textColorGreen"],
            ["blue", "composer.textColorBlue"],
            ["purple", "composer.textColorPurple"],
          ] as const).map(([name, titleKey]) => (
            <button
              key={name}
              type="button"
              className={`alg-color-swatch ${name}${activeMarks.some((active) => sameMark(active, { color: name })) ? " active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleFormat({ color: name })}
              title={t(titleKey)}
              aria-label={t(titleKey)}
              aria-pressed={activeMarks.some((active) => sameMark(active, { color: name }))}
            />
          ))}
        </div>
      )}

      {/* ОДНА УЗКАЯ СТРОКА (постановка Антона 19:11). Кнопки ВНУТРИ
          пилюли, а не блоками вокруг неё: рамка и фон живут на
          .alg-composer-bar, поле внутри — без собственной рамки.
          Баннеры стоят ВЫШЕ строки и её форму не меняют, поэтому при
          ответе/редактировании ничего не прыгает. */}
      {voice.phase === "idle" && (
      <div style={s.composerOuter}>
        <div className="alg-composer-bar">
        {onSendFile && !editing && (
          <>
            <input
              ref={fileRef}
              type="file"
              style={{ display: "none" }}
              data-testid="file-input"
              onChange={(e) => stageFiles(Array.from(e.target.files ?? []))}
            />
            <button
              type="button"
              className="alg-composer-btn"
              disabled={disabled || busy}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                // Меню над кнопкой, а не под: композер внизу окна.
                setAttachMenuAt({ x: r.left, y: r.top - 8 });
              }}
              data-testid="attach"
              title={t("composer.attachFile")}
            >
              {/* Плюсик вместо скрепки: один вход во вложения, а не россыпь кнопок. */}
              <IconPlus size={20} />
            </button>
            <button
              type="button"
              className="alg-composer-btn"
              disabled={disabled || busy}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                // Над кнопкой, а не под: композер внизу окна, вниз места нет.
                setEmojiAt({ x: r.left, y: r.top - 368 });
              }}
              data-testid="emoji-button"
              title={t("composer.emoji")}
            >
              <IconSmile size={20} />
            </button>
          </>
        )}
        <div className={`alg-composer-input${disabled ? " disabled" : ""}`}>
          <div
            ref={previewRef}
            className={`alg-composer-preview${composing ? " composing" : ""}`}
            aria-hidden="true"
            data-testid="composer-format-preview"
          >
            {previewSegments.map((segment, i) => segment.type === "text" ? (
              <span
                key={`${i}-${segment.text.length}`}
                className={previewMarkClasses(segment.marks)}
              >
                {segment.text}
              </span>
            ) : segment.displayName)}
            {/* Keeps a final newline measurable without displaying content. */}
            <span className="alg-composer-preview-sentinel">&#8203;</span>
          </div>
          <textarea
            ref={ref}
            className={`alg-composer-textarea${composing ? " composing" : ""}`}
            value={text}
            disabled={disabled}
            placeholder={
              disabled
                ? t("composer.disabledPlaceholder")
                : editing
                  ? t("ui.redaktiruyte_soobschenie")
                  : replyTo
                    ? t("ui.otvet")
                    : ""
            }
            data-testid="composer"
            rows={1}
            onChange={onChange}
            onBeforeInput={(e) => {
              pendingInput.current = {
                start: e.currentTarget.selectionStart ?? 0,
                end: e.currentTarget.selectionEnd ?? e.currentTarget.selectionStart ?? 0,
                inputType: (e.nativeEvent as InputEvent).inputType || "",
              };
            }}
            onKeyDown={onKeyDown}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={() => {
              setComposing(false);
              caretAfterCommit.confirm();
            }}
            onScroll={(e) => {
              const preview = previewRef.current;
              if (!preview) return;
              preview.scrollTop = e.currentTarget.scrollTop;
              preview.scrollLeft = e.currentTarget.scrollLeft;
            }}
            onPaste={(e) => {
              // Скриншот из буфера (Cmd+V) — тот же путь, что и drop.
              // Берём ТОЛЬКО файлы; если в буфере текст, не мешаем вставке.
              const files = Array.from(e.clipboardData?.files ?? []);
              if (files.length > 0) {
                e.preventDefault();
                stageFiles(files);
              }
            }}
          />
        </div>
        <button
          type="button"
          className="alg-composer-btn send"
          disabled={disabled || !sendable || busy}
          onClick={() => void submit()}
          data-testid="send"
          title={editing ? t("common.save") : t("composer.sendButton")}
        >
          {/* В режиме редактирования — галочка, в обычном — самолётик.
              Смысл кнопки меняется, но она остаётся в той же линейке. */}
          {editing ? <IconCheck size={20} /> : <IconSend size={20} />}
        </button>
        </div>
      </div>
      )}

      {/* Меню вложений (плюсик) — position:fixed, поверх композера. */}
      {attachMenuAt && (
        <div
          className="alg-attach-menu"
          style={{
            left: attachMenuAt.x,
            top: attachMenuAt.y,
            transform: "translateY(-100%)",
          }}
          data-testid="attach-menu"
        >
          <button
            type="button"
            className="alg-attach-item"
            data-testid="attach-photo"
            onClick={() => {
              setAttachMenuAt(null);
              openAttachPicker("media");
            }}
          >
            <IconImage size={18} />{t("composer.photoVideo")}</button>
          <button
            type="button"
            className="alg-attach-item"
            data-testid="attach-file"
            onClick={() => {
              setAttachMenuAt(null);
              openAttachPicker("file");
            }}
          >
            <IconFile size={18} />{t("composer.file")}</button>
          {onSendVoice && (
            <button
              type="button"
              className="alg-attach-item"
              data-testid="attach-voice"
              onClick={() => {
                setAttachMenuAt(null);
                void startVoice();
              }}
            >
              <IconMic size={18} />{t("composer.voiceMessage")}</button>
          )}
        </div>
      )}

      {emojiAt && (
        <EmojiPicker
          anchor={emojiAt}
          onClose={() => setEmojiAt(null)}
          onPick={(ch) => {
            // Вставка В КУРСОР, а не в конец: человек мог вернуться
            // в середину строки. Пикер не закрываем — подряд ставят
            // несколько эмодзи, и переоткрывать каждый раз мучительно.
            const el = ref.current;
            const at = el ? (el.selectionStart ?? text.length) : text.length;
            const end = el ? (el.selectionEnd ?? at) : at;
            setText(text.slice(0, at) + ch + text.slice(end));
            requestAnimationFrame(() => {
              el?.focus();
              const p = at + ch.length;
              el?.setSelectionRange(p, p);
            });
          }}
        />
      )}

    </div>
  );
}
